import { findingBriefTourTarget } from './findingVisualTheme';
import type { ReviewReason } from './reviewReason.types';

export type ReviewPathTourStep = {
  id: string;
  target: string;
  title: string;
  /** One short line — bullets carry the detail. */
  description: string;
  points: string[];
  tip?: string;
  tipPoints?: string[];
};

export function concernReviewEvidenceTarget(findingId: string): string {
  return `concern-review-evidence-${findingId}`;
}

/** Spotlight tour for the employment “what’s happening” summary — how to use the review flow. */
export function buildWhatsHappeningReviewPathSteps(
  findings: ReviewReason[],
  issueCount: number,
): ReviewPathTourStep[] {
  const firstFinding = findings[0];
  const issueLine =
    issueCount === 1
      ? '1 issue needs your attention'
      : `${issueCount} issues need your attention`;

  return [
    {
      id: 'review-panel',
      target: 'employer-review-panel',
      title: 'Employer review panel',
      description: 'This tab is your workspace for employment verification.',
      points: [
        'Work top to bottom — summary, brief, supporting evidence, then gates.',
        `Track open issues — ${issueLine.toLowerCase()}.`,
        'The guided path walks through each section in order.',
      ],
      tip: 'Scroll is locked during the tour so each highlight stays on the right section.',
    },
    {
      id: 'review-summary',
      target: 'employer-review-summary',
      title: 'Review summary',
      description: 'Start here for the employment verification snapshot.',
      points: [
        'Check the overall status badge (pass, review, or fail).',
        `See how many issues are open — ${issueLine.toLowerCase()}.`,
        'Expand a concern for a short why and what to do.',
      ],
      tip: 'Use this panel to decide whether you can approve or need deeper review.',
    },
    {
      id: 'scan-concerns',
      target: 'employer-concern-list',
      title: 'Scan concerns',
      description: 'Each card is one issue flagged during verification.',
      points: [
        'Red flags need action before you approve employment.',
        'Concerns may be acceptable with explanation or extra evidence.',
        'Work top to bottom — red flags first, then concerns.',
      ],
    },
    {
      id: 'open-issue',
      target: firstFinding ? 'employer-concern-primary' : 'employer-concern-list',
      title: 'Open one issue',
      description: 'Focus on a single concern at a time.',
      points: [
        'Expand a concern to read why it was flagged and what to check.',
        'Click Review evidence to jump to that issue’s verification brief.',
        'Only one brief expands at a time — others stay collapsed.',
      ],
      tipPoints: ['Finish one issue before moving to the next.'],
    },
    {
      id: 'verification-details',
      target: firstFinding
        ? findingBriefTourTarget(firstFinding.id)
        : 'employer-verification-details',
      title: 'Verification brief',
      description: 'Full case summary, review measures, and follow-up for the selected issue.',
      points: [
        'Read the evidence points that support the flag.',
        'Check follow-up items if public signals stay inconclusive.',
        'Use Review evidence again inside the brief for a checkpoint walkthrough.',
      ],
    },
    {
      id: 'gate-analysis',
      target: 'employer-gate-analysis-first-review',
      title: 'Gate checkpoint',
      description: 'Open the gate row when you need proof behind a flag.',
      points: [
        'Compare declared values against registry, bank, and map signals.',
        'Tap Review on a checkpoint row for full evidence.',
        'Gate analysis stays below the brief — open it when the brief is not enough.',
      ],
    },
  ];
}

export function reviewPathTargetForFinding(findingId: string): string {
  return findingBriefTourTarget(findingId);
}
